import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Empty, Input, Modal, Select, Tooltip, message } from 'antd';
import dayjs from 'dayjs';
import { AlertTriangle, CalendarCheck2, ChevronLeft, ChevronRight, Copy, Download, Megaphone, Search, Settings2 } from 'lucide-react';
import Layout from '../../Layout';
import { useHr } from './hrStore';
import { tpApi, useTp } from './tpStore';
import { LEAVE_KIND, branchById, deptOf, fmtD, isPaused, isWorking, normText, positionById, toDay, type Employee } from './hrLogic';
import {
  ASSIGN_STATE, WD, coverage, evalAssign, holidayOf, isLocked, leaveOn, monthOf, shiftHours, shiftLabel, validateAssign, weekDays, weekStart, type Assign,
} from './tpLogic';
import { Avatar, Badge, Kpi, LevelDot, Notes, PageHeader, exportXlsx } from './hrUi';
import { BranchSelect, ShiftChip, done, useTpPerms } from './tpUi';

/** Phân ca theo tuần cho từng chi nhánh: xếp / bỏ ca, chép tuần trước, kiểm tra định mức & bác sĩ phụ trách, công bố lịch */
export default function Roster() {
  const s = useHr();
  const t = useTp();
  const navigate = useNavigate();
  const { canAssign, me } = useTpPerms();
  const [params, setParams] = useSearchParams();
  const go = (p: Record<string, string | null>) => {
    const n = new URLSearchParams(params);
    Object.entries(p).forEach(([k, v]) => (v ? n.set(k, v) : n.delete(k)));
    setParams(n, { replace: true });
  };
  const today = toDay();
  const branchId = params.get('branch') || s.branches.find(b => b.clinic)?.id || s.branches[0]?.id || '';
  const week = weekStart(params.get('w') || today);
  const dept = params.get('dept') || '';
  const q = params.get('q') || '';
  const days = weekDays(week);
  const [pick, setPick] = useState<{ empId: string; date: string } | null>(null);
  const now = Date.now();

  const weekAssigns = useMemo(() => t.assigns.filter(a => a.date >= days[0] && a.date <= days[6]), [t.assigns, days]);
  const rows = useMemo(() => {
    const ids = new Set(weekAssigns.filter(a => a.branchId === branchId).map(a => a.empId));
    return s.employees
      .filter(e => (e.branchId === branchId && days.some(d => isWorking(e, d))) || ids.has(e.id))
      .filter(e => (!dept || deptOf(s, e) === dept) && (!q || normText(`${e.name} ${e.code}`).includes(normText(q))))
      .sort((a, b) => (a.branchId === branchId ? 0 : 1) - (b.branchId === branchId ? 0 : 1) || deptOf(s, a).localeCompare(deptOf(s, b)) || a.code.localeCompare(b.code));
  }, [s, weekAssigns, branchId, dept, q, days]);
  const issues = useMemo(() => coverage(s, t, branchId, days), [s, t, branchId, days]);
  const shifts = t.shifts.filter(x => x.active && (!x.branchIds.length || x.branchIds.includes(branchId)));
  const pub = t.published.find(p => p.branchId === branchId && p.week === week);
  const changedAfter = pub ? weekAssigns.filter(a => a.branchId === branchId && (a.at || 0) > pub.at).length : 0;
  const branchAssigns = weekAssigns.filter(a => a.branchId === branchId);
  const hours = branchAssigns.reduce((n, a) => n + shiftHours(t.shifts.find(x => x.id === a.shiftId)!), 0);
  const deptsHere = Array.from(new Set(s.employees.filter(e => e.branchId === branchId).map(e => deptOf(s, e))));

  const cellOf = (e: Employee, d: string) => weekAssigns.filter(a => a.empId === e.id && a.date === d);
  const copyPrev = () => Modal.confirm({
    title: `Chép lịch tuần ${fmtD(dayjs(week).subtract(7, 'day').format('YYYY-MM-DD'))} sang tuần này?`,
    content: 'Chỉ thêm ca vào ô còn trống; bỏ qua người đang nghỉ phép, đã nghỉ việc hoặc trùng giờ.',
    okText: 'Chép lịch', cancelText: 'Thôi',
    onOk: () => { done(tpApi.copyWeek(branchId, dayjs(week).subtract(7, 'day').format('YYYY-MM-DD'), week, me), 'Đã chép lịch tuần trước'); },
  });
  const publish = () => {
    const crit = issues.filter(i => i.level === 'crit');
    Modal.confirm({
      title: `Công bố lịch tuần ${fmtD(days[0])} – ${fmtD(days[6])}`,
      content: crit.length ? <div className="text-rose-600 text-sm">Còn {crit.length} ca thiếu bác sĩ / không có người. Vẫn công bố?</div> : 'Nhân viên sẽ thấy lịch ca của mình.',
      okText: 'Công bố', cancelText: 'Thôi',
      onOk: () => { done(tpApi.publish(branchId, week, me), 'Đã công bố lịch'); },
    });
  };
  const doExport = () => exportXlsx(`phan_ca_${branchById(s, branchId)?.short}_${dayjs(week).format('DDMM')}`, 'Phân ca', rows.map(e => ({
    'Mã NS': e.code, 'Họ tên': e.name, 'Vị trí': positionById(s, e.positionId)?.name || '',
    ...Object.fromEntries(days.map(d => [`${WD[dayjs(d).day()]} ${dayjs(d).format('DD/MM')}`, cellOf(e, d).map(a => t.shifts.find(x => x.id === a.shiftId)?.code).join('+') || (leaveOn(e, d) ? LEAVE_KIND[leaveOn(e, d)!.r.kind].label : '')])),
  })));

  return (
    <Layout>
      <div className="flex-1 flex flex-col min-w-0 gap-4">
        <PageHeader title="Phân ca" desc="Xếp ca theo tuần từ danh sách nhân sự đang làm — tự kiểm tra nghỉ phép, trùng giờ, giờ nghỉ giữa ca, GPHN và bác sĩ trực từng ca"
          extra={<>
            <Button icon={<Settings2 size={14} />} onClick={() => navigate('/shifts')}>Ca làm</Button>
            <Button icon={<Download size={14} />} onClick={doExport}>Xuất Excel</Button>
            {canAssign && <Button icon={<Copy size={14} />} onClick={copyPrev}>Chép tuần trước</Button>}
            {canAssign && <Button type="primary" icon={<Megaphone size={14} />} onClick={publish}>{pub ? 'Công bố lại' : 'Công bố lịch'}</Button>}
          </>} />

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={<CalendarCheck2 size={18} />} tone="bg-sky-50 text-sky-600" label="Ca trong tuần" value={branchAssigns.length} hint={`${Math.round(hours)} giờ công`} />
          <Kpi icon={<AlertTriangle size={18} />} tone={issues.some(i => i.level === 'crit') ? 'bg-rose-50 text-rose-600' : issues.length ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'} label="Ca thiếu người / bác sĩ" value={issues.length} hint={issues.length ? `${issues.filter(i => i.level === 'crit').length} nghiêm trọng` : 'Đủ định mức'} />
          <Kpi icon={<Megaphone size={18} />} tone={pub ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'} label="Trạng thái lịch" value={pub ? 'Đã công bố' : 'Chưa công bố'} hint={pub ? `${dayjs(pub.at).format('HH:mm DD/MM')} · ${pub.by}${changedAfter ? ` · ${changedAfter} ca sửa sau` : ''}` : 'Nhân viên chưa thấy lịch'} />
          <Kpi icon={<CalendarCheck2 size={18} />} tone="bg-violet-50 text-violet-600" label="Nhân sự trong lịch" value={rows.length} hint={`${rows.filter(e => e.branchId !== branchId).length} người tăng cường`} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <BranchSelect all={false} value={branchId} onChange={v => go({ branch: v, dept: null })} />
          <div className="flex items-center gap-1">
            <Button icon={<ChevronLeft size={14} />} onClick={() => go({ w: dayjs(week).subtract(7, 'day').format('YYYY-MM-DD') })} aria-label="Tuần trước" />
            <Button onClick={() => go({ w: null })} disabled={week === weekStart(today)}>Tuần này</Button>
            <Button icon={<ChevronRight size={14} />} onClick={() => go({ w: dayjs(week).add(7, 'day').format('YYYY-MM-DD') })} aria-label="Tuần sau" />
          </div>
          <span className="text-sm font-medium">{fmtD(days[0])} – {fmtD(days[6])}</span>
          <Select className="w-[170px]" value={dept} onChange={v => go({ dept: v || null })} options={[{ value: '', label: 'Tất cả bộ phận' }, ...s.depts.filter(d => deptsHere.includes(d.id)).map(d => ({ value: d.id, label: d.name }))]} />
          <Input allowClear prefix={<Search size={14} className="text-gray-400" />} placeholder="Tên, mã" className="!w-[180px]" value={q} onChange={e => go({ q: e.target.value || null })} />
          <div className="ml-auto flex flex-wrap gap-1">{shifts.map(sh => <ShiftChip key={sh.id} sh={sh} extra={<span className="font-normal">{shiftLabel(sh)}</span>} />)}</div>
        </div>

        <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-x-auto">
          {rows.length === 0 ? <Empty className="py-10" image={Empty.PRESENTED_IMAGE_SIMPLE} description="Không có nhân sự" /> : (
            <table className="w-full text-sm min-w-[980px] border-collapse">
              <thead>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-xs text-gray-500">
                  <th className="text-left font-medium px-3 py-2 w-[240px] sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">Nhân sự</th>
                  {days.map(d => {
                    const hol = holidayOf(t.time, d);
                    return (
                      <th key={d} className={`font-medium px-1 py-2 text-center ${d === today ? 'bg-sky-50 text-sky-800 dark:bg-sky-900/30 dark:text-sky-200' : ''}`}>
                        <div>{WD[dayjs(d).day()]} {dayjs(d).format('DD/MM')}</div>
                        {hol && <div className="text-[10px] text-rose-600 font-normal truncate">{hol.name}</div>}
                      </th>
                    );
                  })}
                  <th className="font-medium px-2 py-2 text-right w-[80px]">Tuần</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {rows.map(e => {
                  const wk = weekAssigns.filter(a => a.empId === e.id);
                  const h = wk.reduce((n, a) => n + shiftHours(t.shifts.find(x => x.id === a.shiftId)!), 0);
                  return (
                    <tr key={e.id} className="hover:bg-gray-50/60 dark:hover:bg-gray-800/30">
                      <td className="px-3 py-1.5 sticky left-0 bg-white dark:bg-gray-900 z-10">
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar name={e.name} />
                          <div className="min-w-0">
                            <div className="font-medium truncate max-w-[180px]">{e.name}</div>
                            <div className="text-[11px] text-gray-400 truncate max-w-[180px]">{positionById(s, e.positionId)?.name}{e.branchId !== branchId ? ` · từ ${branchById(s, e.branchId)?.short}` : ''}</div>
                          </div>
                        </div>
                      </td>
                      {days.map(d => {
                        const list = cellOf(e, d);
                        const lv = leaveOn(e, d);
                        const off = !isWorking(e, d) || isPaused(e, d);
                        const locked = isLocked(t, monthOf(d), e.branchId);
                        const editable = canAssign && !off && !locked;
                        return (
                          <td key={d} className={`px-1 py-1 align-top text-center ${off ? 'bg-gray-50 dark:bg-gray-800/40' : ''} ${d === today ? 'bg-sky-50/40 dark:bg-sky-900/10' : ''}`}>
                            <button type="button" disabled={!editable} onClick={() => setPick({ empId: e.id, date: d })}
                              className={`w-full min-h-[38px] rounded-md flex flex-wrap items-center justify-center gap-1 p-0.5 ${editable ? 'hover:bg-gray-100 dark:hover:bg-gray-800 cursor-pointer' : 'cursor-default'}`}>
                              {off && <span className="text-[11px] text-gray-300">—</span>}
                              {lv && <Badge className={LEAVE_KIND[lv.r.kind].badge}>{lv.part < 1 ? '½ ' : ''}{LEAVE_KIND[lv.r.kind].label.split(' (')[0]}</Badge>}
                              {list.map(a => <CellChip key={a.id} a={a} here={branchId} now={now} />)}
                              {!off && !lv && !list.length && editable && <span className="text-gray-300 text-lg leading-none">+</span>}
                            </button>
                          </td>
                        );
                      })}
                      <td className={`px-2 py-1.5 text-right text-xs ${h > 48 ? 'text-rose-600 font-semibold' : 'text-gray-500'}`} title={h > 48 ? 'Quá 48 giờ / tuần' : undefined}>{wk.length} ca<div>{Math.round(h)} giờ</div></td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-gray-50 dark:bg-gray-800/50 text-xs">
                  <td className="px-3 py-2 font-medium sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">Định mức ca</td>
                  {days.map(d => {
                    const iss = issues.filter(i => i.date === d);
                    return (
                      <td key={d} className="px-1 py-2 text-center align-top">
                        <div className="flex flex-wrap justify-center gap-1">
                          {shifts.filter(sh => branchAssigns.some(a => a.date === d && a.shiftId === sh.id) || sh.minStaff).map(sh => {
                            const n = branchAssigns.filter(a => a.date === d && a.shiftId === sh.id).length;
                            return <span key={sh.id} className="text-gray-500">{sh.code} <b className="text-gray-800 dark:text-gray-100">{n}</b></span>;
                          })}
                        </div>
                        {iss.length > 0 && (
                          <Tooltip title={<div className="space-y-0.5">{iss.map(i => <div key={i.text}>• {i.text}</div>)}</div>}>
                            <div className="mt-1 inline-flex items-center gap-1 text-[11px] cursor-help">
                              <LevelDot level={iss.some(i => i.level === 'crit') ? 'crit' : 'warn'} />{iss.length} cảnh báo
                            </div>
                          </Tooltip>
                        )}
                      </td>
                    );
                  })}
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </div>
        <div className="text-[11px] text-gray-400">Bấm vào ô để xếp hoặc bỏ ca. Tháng đã chốt công thì khóa lịch. Ca có khám / thủ thuật phải có bác sĩ còn hạn GPHN; nghỉ tối thiểu 12 giờ giữa 2 ca và có ngày nghỉ hằng tuần (BLLĐ 2019 Điều 109, 111).</div>
      </div>
      {pick && <AssignModal empId={pick.empId} date={pick.date} branchId={branchId} onClose={() => setPick(null)} />}
    </Layout>
  );
}

function CellChip({ a, here, now }: { a: Assign; here: string; now: number }) {
  const s = useHr();
  const t = useTp();
  const sh = t.shifts.find(x => x.id === a.shiftId);
  if (!sh) return null;
  const ev = evalAssign(a, sh, { now, grace: t.time.graceMin });
  const dot = ['late', 'early', 'late_early'].includes(ev.state) ? 'bg-amber-500' : ['absent', 'forgot_in', 'forgot_out'].includes(ev.state) ? 'bg-rose-500' : ['ok'].includes(ev.state) ? 'bg-emerald-500' : '';
  return (
    <ShiftChip sh={sh} title={`${sh.name} ${shiftLabel(sh)} · ${ASSIGN_STATE[ev.state].label}${a.inAt ? ` · vào ${dayjs(a.inAt).format('HH:mm')}` : ''}${a.outAt ? ` · ra ${dayjs(a.outAt).format('HH:mm')}` : ''}`}
      extra={<>{a.branchId !== here && <span className="font-normal">@{branchById(s, a.branchId)?.short}</span>}{dot && <span className={`w-1.5 h-1.5 rounded-full ${dot}`} />}</>} />
  );
}

function AssignModal({ empId, date, branchId, onClose }: { empId: string; date: string; branchId: string; onClose: () => void }) {
  const s = useHr();
  const t = useTp();
  const { me } = useTpPerms();
  const e = s.employees.find(x => x.id === empId)!;
  const today = toDay();
  const mine = t.assigns.filter(a => a.empId === empId && a.date === date);
  const options = t.shifts.filter(x => x.active && (!x.branchIds.length || x.branchIds.includes(branchId)) && !mine.some(a => a.shiftId === x.id))
    .map(sh => ({ sh, v: validateAssign(s, t, { empId, date, shiftId: sh.id, branchId }, today) }));
  return (
    <Modal open title={`${e.name} · ${WD[dayjs(date).day()]} ${fmtD(date)}`} onCancel={onClose} footer={null} width={520}>
      <div className="space-y-4 text-sm">
        {mine.length > 0 && (
          <div className="space-y-1.5">
            <div className="text-xs text-gray-500">Ca đã xếp</div>
            {mine.map(a => {
              const sh = t.shifts.find(x => x.id === a.shiftId)!;
              return (
                <div key={a.id} className="flex items-center gap-2 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2">
                  <ShiftChip sh={sh} /><span>{sh.name} {shiftLabel(sh)}</span>
                  {a.branchId !== branchId && <span className="text-xs text-gray-400">tại {branchById(s, a.branchId)?.short}</span>}
                  {a.inAt && <span className="text-xs text-gray-500">vào {dayjs(a.inAt).format('HH:mm')}{a.outAt ? ` · ra ${dayjs(a.outAt).format('HH:mm')}` : ''}</span>}
                  <Button size="small" danger type="text" className="!ml-auto" onClick={() => { if (done(tpApi.unassign(a.id, me), 'Đã bỏ ca')) onClose(); }}>Bỏ ca</Button>
                </div>
              );
            })}
          </div>
        )}
        <div className="space-y-1.5">
          <div className="text-xs text-gray-500">{mine.length ? 'Thêm ca' : 'Chọn ca'}</div>
          {options.map(({ sh, v }) => (
            <div key={sh.id} className={`rounded-lg border px-3 py-2 ${v.errors.length ? 'border-gray-200 dark:border-gray-700 opacity-80' : 'border-gray-200 dark:border-gray-700 hover:border-[#3e91ab]'}`}>
              <div className="flex items-center gap-2">
                <ShiftChip sh={sh} /><span>{sh.name}</span><span className="text-xs text-gray-400">{shiftLabel(sh)} · {sh.work} công</span>
                <Button size="small" type="primary" className="!ml-auto" disabled={v.errors.length > 0}
                  onClick={() => { const r = tpApi.assign({ empId, date, shiftId: sh.id, branchId }, me); if (done(r)) { message.success(`Đã xếp ${sh.name}`); onClose(); } }}>Xếp ca</Button>
              </div>
              {(v.errors.length > 0 || v.warnings.length > 0) && <div className="mt-2"><Notes errors={v.errors} warnings={v.warnings.filter(w => !w.startsWith('Làm tăng cường'))} /></div>}
            </div>
          ))}
          {!options.length && <div className="text-xs text-gray-400">Không còn ca phù hợp</div>}
        </div>
      </div>
    </Modal>
  );
}
